// @flow

import { useLocation } from 'react-router-dom';
import { createSelector } from 'reselect';
import { superpathjoin } from 'superpathjoin';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import { selectCartUUID } from '@gandi/cart';
import { selectFromConfig } from '@gandi/redux-config';
import { selectCurrentId } from '@gandi/redux-sharing';
import { localeSelector as selectLocale } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';
import type { LayoutUrls } from 'src/modules/app/selectors';
import { pathjoin } from 'src/modules/pathjoin';
import type { FullState } from 'src/types';

export const selectShopURL: (state: FullState) => string =
  selectFromConfig<string>('app.layout.urls.shop', 'https://shop.gandi.net');

export const selectAdminURL: (state: FullState) => string =
  selectFromConfig<string>('app.layout.urls.admin', 'https://admin.gandi.net');

const selectBaseWebURL: (state: FullState) => string = selectFromConfig<string>(
  'app.layout.urls.public_site',
  'https://www.gandi.net',
);
export const selectWebURL: (state: FullState) => string = createSelector(
  [selectBaseWebURL, selectLocale],
  (wwwUrl, locale) => `${wwwUrl}/${locale}`,
);

const selectBaseWhoisUrl = selectFromConfig<string>(
  'app.layout.urls.whois',
  'https://whois.gandi.net',
);
export const selectWhoisUrl: (state: FullState) => string = createSelector(
  [selectBaseWhoisUrl, selectLocale],
  (whois, locale) => `${whois}/${locale}/results`,
);

/**
 * Compute and return a fully qualified URL, including the current app context.
 * Example call: `selectShopContextURL(state, 'payment-return')`
 * Example output: `https://shop.gandi.net/fr/[{sharingId}]/payment-return`
 */
export const selectShopContextURL: (state: FullState, to?: string) => string =
  createSelector(
    [selectShopURL, selectLocale, selectCurrentId, (_, to) => to || ''],
    (shopUrl, ...paths) => superpathjoin(shopUrl, ...paths),
  );

export const selectAdminBillingUrl = (state: FullState): string => {
  const adminUrl = selectAdminURL(state);
  return superpathjoin(adminUrl, 'billing', selectCurrentId(state));
};

export const selectOrderUrl = (
  state: FullState,
  { orderId }: { orderId: string },
): string => {
  const baseUrl = selectAdminBillingUrl(state);
  return superpathjoin(baseUrl, 'orders', orderId);
};

export function getLinkTarget(url: string): { href: string } | { to: string } {
  return url.startsWith('http') ? { href: url } : { to: url };
}

/** Return either the legacy authenticated certificate URL of the shop or the public page. */
export const selectCertificateURL: (state: FullState) => string =
  createSelector(
    [selectCartUUID, selectWebURL, selectIsAuthenticatedUser],
    (cartID, wwwUrl, isAuthenticated) =>
      isAuthenticated
        ? `/certificate/create${cartID ? `/${cartID}` : ''}`
        : `${wwwUrl}/security`,
  );

/** Return the URL of the "GandiCloud VPS" admin. */
const selectAdminGostackURL: (state: FullState) => string = createSelector(
  [selectAdminURL, selectCurrentId],
  (adminUrl, currentId) =>
    currentId ? `${adminUrl}/gostack/${currentId}` : `${adminUrl}/gostack`,
);

/** Return either the URL of the "GandiCloud VPS" admin or public page. */
export const selectGostackURL: (state: FullState) => string = createSelector(
  [selectAdminGostackURL, selectWebURL, selectIsAuthenticatedUser],
  (adminGostackUrl, wwwUrl, isAuthenticated) =>
    isAuthenticated ? adminGostackUrl : `${wwwUrl}/cloud/vps`,
);

const selectURLs = selectFromConfig<LayoutUrls>('app.layout.urls', {});

const selectLoginCallbackUrl: (
  state: FullState,
  pathname: string,
  search: string,
) => string = createSelector(
  [
    selectLocale,
    selectURLs,
    selectShopURL,
    (_, pathname) => pathname,
    (_, __, search) => search,
  ],
  (locale, urls, shopUrl, pathname, search) => {
    const redirectUrl = pathjoin(shopUrl, locale, pathname, search);

    const href = `${urls.login}?locale=${locale}&redirect=${encodeURIComponent(
      redirectUrl,
    )}`;

    return href;
  },
);

export function useLoginCallbackUrl(): string {
  const { pathname, search } = useLocation();

  const href = useAppSelector(state =>
    selectLoginCallbackUrl(state, pathname, search),
  );
  return href;
}
